import {
  addToast,
  Button,
  ErrorToast,
  SuccessToast,
} from '@otter-ai/den-components';
import { MicrosoftIcon } from '@otter-ai/den-icons';
import { Screen } from '@otter-ai/telemetry';
import { useTranslations } from 'next-intl';
import type { PopupAuthErrors } from '@/constants/auth';
import { FormattedText } from '@/shared/components/FormattedText';
import { useGetCurrentUser } from '@/shared/hooks/current-user/useGetCurrentUser';
import { useAuthPopupErrorMessages } from '@/shared/hooks/useAuthPopupErrorMessages';
import { useMicrosoftCalendar } from '@/shared/hooks/useMicrosoftCalendar';
import { BUILT_IN_APPS, type SyncedAccount } from '@/shared/types/apps';
import { FailedMicrosoftAuth } from '@/shared/utils/microsoftUtils';
import { descriptionFormatRules } from '../../shared/utils/text';
import { AppRowLayout } from '../components/AppRowLayout';
import { useDisconnectApp } from '../hooks/useDisconnectApp';
import { useSyncedAccounts } from '../hooks/useSyncedAccounts';
import { MICROSOFT_TEST_IDS } from '../test-ids/microsoft';
import { trackMicrosoft } from '../tracking/microsoft-tracking';
import { DisconnectedButton } from './DisconnectedButton';

/**
 * The row for Microsoft on the apps settings page.
 *
 * [Figma](https://www.figma.com/design/jEoBjkGRTOV4ETZ4Qli3Bl/Account-Settings-React?node-id=63-6505&m=dev)
 */
export function MicrosoftAppRow() {
  const t = useTranslations('settings:integrations.apps.microsoft');
  const { userId } = useGetCurrentUser();
  const { connectMicrosoftCalendar } = useMicrosoftCalendar({
    userid: userId,
    screen: Screen.apps,
  });
  const { accounts, isLoading: loadingConnectedAccount } = useSyncedAccounts({
    service: BUILT_IN_APPS.MICROSOFT,
  });
  const { disconnectAppAsync } = useDisconnectApp();
  const { showAuthPopupErrorToast } = useAuthPopupErrorMessages('Microsoft');

  const onClickAdd = async () => {
    trackMicrosoft.main.connectMicrosoft();
    try {
      await connectMicrosoftCalendar();
      addToast(<SuccessToast description={t('accountConnected')} />);
    } catch (err) {
      showAuthPopupErrorToast(err);
    }
  };

  const handleDisconnect = async (account: SyncedAccount) => {
    trackMicrosoft.main.disconnectMicrosoft();
    try {
      await disconnectAppAsync({ service: BUILT_IN_APPS.MICROSOFT, account });
      addToast(
        <SuccessToast
          description={t('successfulDisconnected', {
            accountName: account.account,
          })}
        />,
      );
      return true;
    } catch (err) {
      displayDisconnectionErrorToastForError(err, t);
      return false;
    }
  };

  const description = (
    <FormattedText>
      {t.rich(
        'description',
        descriptionFormatRules({
          links: { 'help-link': { href: t('helpUrl'), target: '_blank' } },
        }),
      )}
    </FormattedText>
  );

  return (
    <AppRowLayout
      appRowId="microsoft-app"
      Icon={MicrosoftIcon}
      tags={{
        enterpriseTag: true,
      }}
      title="Microsoft"
      description={description}
      buttons={
        // Multiple Microsoft accounts are supported
        <>
          {loadingConnectedAccount && (
            <Button disabled={true} data-testid={MICROSOFT_TEST_IDS.addButton}>
              {t('addButton')}
            </Button>
          )}
          {!loadingConnectedAccount && (
            <Button
              onClick={onClickAdd}
              data-testid={MICROSOFT_TEST_IDS.addButton}
            >
              {t('addButton')}
            </Button>
          )}
        </>
      }
      linkedAccounts={accounts.map((account) => ({
        accountName: account.account,
        accountId: account.account,
        button: (
          <DisconnectedButton
            disconnectButtonText={t('disconnectButton')}
            disconnectAction={() => handleDisconnect(account)}
            tooltipCaption={t('disconnectTooltip', {
              accountName: account.account,
            })}
            data-testid={MICROSOFT_TEST_IDS.disconnectButton}
          />
        ),
      }))}
    />
  );
}

function displayDisconnectionErrorToastForError(
  error: unknown,
  t: ReturnType<typeof useTranslations>,
) {
  if (error instanceof FailedMicrosoftAuth) {
    trackMicrosoft.main.connectionFailed();
    const messages: Record<PopupAuthErrors, string | null> = {
      POPUP_CLOSED: null,
      UNSUPPORTED: t('microsoftIsUnsupported'),
      FAILED_INITIALIZATION: t('failedInitialization'),
      USER_CANCELLED: null,
      OPERATION_FAILED: t('failedAuthentication'),
      POPUP_FAILED_TO_OPEN: t('popupFailedToOpen'),
    };

    const toastMessage = messages[error.popupAuthError];

    if (toastMessage === null) {
      return;
    }

    addToast(<ErrorToast description={toastMessage} />);
  } else {
    addToast(<ErrorToast description={t('failedDisconnect')} />);
  }
}
